import React, { createContext, useContext } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';
import { useNavigate, useLocation, Link } from 'react-router-dom';

// Create context for menu state
interface MobileMenuContextType {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}

const MobileMenuContext = createContext<MobileMenuContextType | undefined>(undefined);

export const useMobileMenu = () => {
  const context = useContext(MobileMenuContext);
  if (context === undefined) {
    throw new Error('useMobileMenu must be used within a MobileMenuProvider');
  }
  return context;
};

export const MobileMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  
  return (
    <MobileMenuContext.Provider value={{ isMenuOpen, setIsMenuOpen }}>
      {children}
    </MobileMenuContext.Provider>
  );
};

const MobileNav = styled.div<{ $isVisible: boolean; $isDarkSection: boolean; $isMenuOpen: boolean }>`
  display: none;
  position: fixed;
  top: 20px;
  left: 0;
  right: 0;
  z-index: 103;
  padding: 0 20px;
  justify-content: space-between;
  align-items: center;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  opacity: ${props => (props.$isVisible || props.$isMenuOpen) ? 1 : 0};
  visibility: ${props => (props.$isVisible || props.$isMenuOpen) ? 'visible' : 'hidden'};

  ${props => props.$isMenuOpen && `
    opacity: 1 !important;
    visibility: visible !important;
  `}

  @media (max-width: 1050px) {
    display: flex;
  }
`;

const MobileNavLogo = styled.img<{ $isDarkSection: boolean }>`
  height: 30px;
  width: auto;
  transition: opacity 0.3s ease;
  cursor: pointer;
`;

const HamburgerMenu = styled.div<{ $isDarkSection: boolean; $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  cursor: pointer;
  z-index: 104;
  position: relative;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  
  span {
    width: 25px;
    height: 3px;
    background-color: ${props => props.$isOpen ? colors.primary.gold : (props.$isDarkSection ? colors.primary.gold : colors.neutral.darkBrown)};
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
    
    &:nth-child(1) {
      transform: ${props => props.$isOpen ? 'rotate(45deg) translate(6px, 6px)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      opacity: ${props => props.$isOpen ? '0' : '1'};
    }
    
    &:nth-child(3) {
      transform: ${props => props.$isOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'rotate(0)'};
    }
  }
`;

const MenuBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 100;
  
  @media (min-width: 1051px) {
    display: none;
  }
`;

const MenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  background-color: ${colors.neutral.darkBrown};
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 101;
  padding: 80px 40px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MenuItem = styled.a`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  color: ${colors.primary.gold};
  text-decoration: none;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  cursor: pointer;
  padding-bottom: 8px;
  
  &:hover {
    transform: translateX(10px);
    color: ${colors.primary.lightGold || colors.primary.gold};
  }
`;

const MenuLink = styled(Link)`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  color: ${colors.primary.gold};
  text-decoration: none;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  cursor: pointer;
  padding-bottom: 8px;
  
  &:hover {
    transform: translateX(10px);
    color: ${colors.primary.lightGold || colors.primary.gold};
  }
`;

const MobileNavigation: React.FC = () => {
  const [isMobileNavVisible, setIsMobileNavVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);
  const [isDarkSection, setIsDarkSection] = React.useState(true);
  const { isMenuOpen, setIsMenuOpen } = useMobileMenu();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogoClick = () => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleHamburgerClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsMenuOpen(false);
  };

  const handleRSVPNavigation = () => {
    navigate('/rsvp');
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleDetailsScroll = () => {
    scrollToSection('schedule');
  };

  const handleTravelNavigation = () => {
    navigate('/travel');
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleRegistryNavigation = () => {
    navigate('/registry');
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleHomeNavigation = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const scrollToSection = (sectionId: string) => {
    const attemptScroll = (retries = 0, maxRetries = 10) => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Small delay to ensure any layout shifts are complete
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      } else if (retries < maxRetries) {
        // Retry after 100ms if element not found
        setTimeout(() => attemptScroll(retries + 1, maxRetries), 100);
      }
    };

    if (location.pathname !== '/') {
      navigate('/');
      // Wait longer for navigation to complete and page to render
      setTimeout(() => attemptScroll(), 300);
    } else {
      attemptScroll();
    }
  };

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Always update lastScrollY
      setLastScrollY(currentScrollY);
      
      // Don't change nav visibility when menu is open
      if (isMenuOpen) {
        return;
      }
      
      if (currentScrollY <= 10) {
        setIsMobileNavVisible(true);
      } else if (currentScrollY < lastScrollY) {
        setIsMobileNavVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 30) {
        setIsMobileNavVisible(false);
      }
    };

    const detectSection = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      
      const currentPath = window.location.pathname;
      if (currentPath === '/rsvp') {
        setIsDarkSection(false);
        return;
      }
      
      const darkSectionEnd = windowHeight * 0.9;
      
      if (scrollY < darkSectionEnd) {
        setIsDarkSection(true);
      } else {
        setIsDarkSection(false);
      }
    };

    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          detectSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    detectSection();
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [lastScrollY, isMenuOpen]);

  // Ensure nav is visible when menu opens
  React.useEffect(() => {
    if (isMenuOpen) {
      setIsMobileNavVisible(true);
    }
  }, [isMenuOpen]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMenuOpen && !target.closest('[data-mobile-menu]')) {
        setIsMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMenuOpen]);

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (isMenuOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Prevent scrolling on body and html
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Prevent touch scrolling on mobile
      const preventTouchMove = (e: TouchEvent) => {
        e.preventDefault();
      };
      
      // Prevent wheel scrolling
      const preventWheel = (e: WheelEvent) => {
        e.preventDefault();
      };
      
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      document.addEventListener('wheel', preventWheel, { passive: false });
      
      return () => {
        // Restore scrolling
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.documentElement.style.overflow = '';
        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('wheel', preventWheel);
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMenuOpen]);

  return (
    <>
      <MobileNav $isVisible={isMobileNavVisible} $isDarkSection={isDarkSection} $isMenuOpen={isMenuOpen} data-mobile-menu>
        <MobileNavLogo 
          src={isDarkSection ? "/images/agave-full-logo.png" : "/images/agave-full-logo-brn.png"}
          alt="Agave Logo" 
          $isDarkSection={isDarkSection}
          onClick={handleLogoClick}
        />
        <HamburgerMenu 
          $isDarkSection={isDarkSection} 
          $isOpen={isMenuOpen}
          onClick={handleHamburgerClick}
          data-mobile-menu
        >
          <span></span>
          <span></span>
          <span></span>
        </HamburgerMenu>
      </MobileNav>

      <MenuBackdrop 
        $isOpen={isMenuOpen} 
        onClick={() => setIsMenuOpen(false)}
        data-mobile-menu
      />

      <MenuOverlay $isOpen={isMenuOpen} data-mobile-menu>
        <MenuLink 
          to="/" 
          onClick={() => handleMenuItemClick(handleHomeNavigation)}
        >
          Home
        </MenuLink>
        <MenuItem 
          onClick={() => handleMenuItemClick(handleDetailsScroll)}
        >
          Schedule
        </MenuItem>
        <MenuLink 
          to="/travel"
          onClick={() => handleMenuItemClick(handleTravelNavigation)}
        >
          Details
        </MenuLink>
        <MenuLink 
          to="/rsvp" 
          onClick={() => handleMenuItemClick(handleRSVPNavigation)}
        >
          RSVP
        </MenuLink>
        <MenuLink 
          to="/registry"
          onClick={() => handleMenuItemClick(handleRegistryNavigation)}
        >
          Registry
        </MenuLink>
      </MenuOverlay>
    </>
  );
};

export default MobileNavigation; 